import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import {
  TrendingDown, RefreshCw, UserMinus, AlertCircle, ArrowDownRight,
  ArrowUpRight, Minus, FileDown, Loader2,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, ComposedChart, Line, XAxis, YAxis,
  Tooltip, CartesianGrid, Legend, LineChart,
} from "recharts";
import { toast } from "sonner";
import { PageHero, KpiCard, Section, GridSkeleton } from "@/components/AnalyticsCommon";
import GlobalDashboardFilters, { DEFAULT_FILTERS, filtersToParams } from "@/components/GlobalDashboardFilters";

const TIPO_LABEL = {
  pedido_demissao: "Pedido de demissão",
  dispensa_sem_justa_causa: "Dispensa sem justa causa",
  dispensa_com_justa_causa: "Dispensa com justa causa",
  rescisao_acordada: "Rescisão acordada",
  termino_contrato_experiencia: "Término contrato experiência",
  termino_contrato_determinado: "Término contrato determinado",
  termino_contrato_voluntario: "Término voluntário",
  aposentadoria: "Aposentadoria",
};

// Retorna a cor do acento com base em duas faixas: bom (<goodMax), moderado (<warnMax), crítico (>=warnMax).
// Substitui ternários aninhados mantendo lógica idêntica ao código anterior.
function turnoverBand(value, goodMax, warnMax) {
  if (value < goodMax) return "emerald";
  if (value < warnMax) return "ochre";
  return "rose";
}

function DeltaBadge({ value, suffix = "%", invert = true }) {
  const isZero = !value || Math.abs(value) < 0.01;
  const positive = value > 0;
  const good = invert ? !positive : positive; // for turnover, lower is better
  const getColor = () => {
    if (isZero) return "text-slate-500 bg-slate-100";
    if (good) return "text-emerald-700 bg-emerald-50";
    return "text-rose-700 bg-rose-50";
  };
  const getIcon = () => {
    if (isZero) return Minus;
    if (positive) return ArrowUpRight;
    return ArrowDownRight;
  };
  const color = getColor();
  const Icon = getIcon();
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-semibold ${color}`}>
      <Icon className="h-3 w-3" />
      {value > 0 ? "+" : ""}{value}{suffix}
    </span>
  );
}

export default function TurnoverPage() {
  const { companies, selected } = useCompany();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfPeriod, setPdfPeriod] = useState("mensal");
  const [pdfTipo, setPdfTipo] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/performance/turnover", { params: filtersToParams(filters) });
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const exportPdf = async () => {
    setPdfLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("period", pdfPeriod);
      params.set("year", String(filters.year || new Date().getFullYear()));
      params.set("grouped", "true");
      if (pdfTipo) params.set("tipo", pdfTipo);
      if (selected && selected !== "all") params.set("company_id", selected);
      const resp = await api.get(`/turnover/report.pdf?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const tipoTag = pdfTipo ? `-${pdfTipo.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 20)}` : "";
      a.download = `turnover-${pdfPeriod}-${filters.year || new Date().getFullYear()}${tipoTag}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Relatório ${pdfPeriod}${pdfTipo ? " · " + pdfTipo : ""} gerado`);
    } catch (e) {
      toast.error("Falha ao gerar PDF de Turnover");
    } finally { setPdfLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [JSON.stringify(filters)]);

  const k = data?.kpis;
  const breakdown = k ? [
    { name: "Voluntário", value: k.voluntarios, color: "#C5A059" },
    { name: "Involuntário", value: k.involuntarios, color: "#234935" },
    { name: "Outros", value: k.outros, color: "#94A3B8" },
  ].filter((b) => b.value > 0) : [];

  // YoY series: pivot by year for comparative chart
  const yoyPivot = (() => {
    if (!data?.yoy_series?.length) return null;
    const byMonth = {};
    data.yoy_series.forEach((r) => {
      const key = r.month_num;
      byMonth[key] = byMonth[key] || { month_num: key, month_label: r.month.split("/")[0] };
      byMonth[key][`y${r.year}`] = r.turnover_pct;
      byMonth[key][`d${r.year}`] = r.desligamentos;
    });
    const years = [...new Set(data.yoy_series.map((r) => r.year))].sort();
    const rows = Object.values(byMonth).sort((a, b) => a.month_num - b.month_num);
    return { rows, years };
  })();

  return (
    <div className="space-y-4 pb-10" data-testid="turnover-page">
      <PageHero
        kicker={<><TrendingDown className="inline h-3 w-3 mr-1.5 -mt-0.5" /> Performance & Analytics</>}
        title="Indicadores de Turnover"
        description="Rotatividade mensal, anual, comparativos YoY/MoM, ranking de setores e cargos."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={pdfPeriod}
              onChange={(e) => setPdfPeriod(e.target.value)}
              className="h-9 rounded-md border border-white/20 bg-white/5 text-white text-[12.5px] px-2"
              data-testid="tov-pdf-period"
              title="Período do relatório"
            >
              <option value="mensal">Mensal</option>
              <option value="trimestral">Trimestral</option>
              <option value="anual">Anual (5 anos)</option>
            </select>
            <select
              value={pdfTipo}
              onChange={(e) => setPdfTipo(e.target.value)}
              className="h-9 rounded-md border border-white/20 bg-white/5 text-white text-[12.5px] px-2"
              data-testid="tov-pdf-tipo"
              title="Filtro por tipo de desligamento"
            >
              <option value="">Todos os tipos</option>
              <option value="Pedido de demissão">Pedido de demissão</option>
              <option value="Dispensa sem justa causa">Dispensa sem justa causa</option>
              <option value="Dispensa com justa causa">Dispensa com justa causa</option>
              <option value="Término de contrato">Término de contrato</option>
              <option value="Acordo entre as partes">Acordo entre as partes</option>
              <option value="Outros">Outros</option>
            </select>
            <Button size="sm" variant="outline" onClick={exportPdf} disabled={pdfLoading}
                    className="h-10 border-white/20 bg-white/5 text-white hover:bg-white/15"
                    data-testid="tov-pdf-btn" title="Gera relatório A4 paisagem com gráfico + lista agrupada por setor">
              {pdfLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <FileDown className="h-3.5 w-3.5 mr-1.5" />}
              PDF
            </Button>
            <Button size="sm" variant="outline" onClick={load}
                    className="h-10 border-white/20 bg-white/5 text-white hover:bg-white/15"
                    data-testid="tov-refresh">
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
        }
      />

      <GlobalDashboardFilters
        value={filters}
        onChange={setFilters}
        companies={companies}
        testIdPrefix="tov-filters"
      />

      {loading || !k ? <GridSkeleton rows={4} /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="tov-kpis">
            <KpiCard testId="tov-period" label={`Turnover · ${data.period?.label || "—"}`}
                    value={`${k.turnover_periodo_pct}%`}
                    sub={`${k.desligados_periodo} desligamentos no período`} icon={TrendingDown}
                    accent={turnoverBand(k.turnover_periodo_pct, 1, 3)} />
            <KpiCard testId="tov-monthly" label="Turnover mensal" value={`${k.turnover_monthly_pct}%`}
                    sub={`${k.desligados_mes} no mês atual`} icon={TrendingDown}
                    accent={turnoverBand(k.turnover_monthly_pct, 1, 3)} />
            <KpiCard testId="tov-annual" label="Turnover anual" value={`${k.turnover_annual_pct}%`}
                    sub={`${k.desligados_12m} no acumulado 12m`} icon={ArrowDownRight}
                    accent={turnoverBand(k.turnover_annual_pct, 8, 15)} />
            <KpiCard testId="tov-voluntary" label="Voluntários × Involuntários"
                    value={`${k.voluntarios} / ${k.involuntarios}`}
                    sub={`${k.voluntarios_pct}% voluntários`} icon={UserMinus} accent="ochre" />
          </div>

          {data.comparative && (
            <Section kicker="Comparativo" title={`${data.comparative.current.label} × ${data.comparative.previous.label}`}
                     testId="tov-comparative">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="rounded-lg border border-border bg-emerald-50/40 p-3">
                  <div className="text-[10.5px] uppercase tracking-wide text-emerald-800 font-semibold mb-1">Atual</div>
                  <div className="text-2xl font-bold text-[#234935] tabular-nums">{data.comparative.current.turnover_pct}%</div>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    {data.comparative.current.desligamentos} desligados · {data.comparative.current.admissoes} admissões
                  </div>
                </div>
                <div className="rounded-lg border border-border bg-slate-50/60 p-3">
                  <div className="text-[10.5px] uppercase tracking-wide text-slate-700 font-semibold mb-1">{data.comparative.previous.label}</div>
                  <div className="text-2xl font-bold text-slate-700 tabular-nums">{data.comparative.previous.turnover_pct}%</div>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    {data.comparative.previous.desligamentos} desligados · {data.comparative.previous.admissoes} admissões
                  </div>
                </div>
                <div className="rounded-lg border border-border bg-white p-3">
                  <div className="text-[10.5px] uppercase tracking-wide text-muted-foreground mb-1">Variação</div>
                  <div className="flex items-center gap-2 mb-1">
                    <DeltaBadge value={data.comparative.delta_turnover_pct} suffix="%" invert />
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {data.comparative.delta_desligamentos > 0 ? "+" : ""}{data.comparative.delta_desligamentos} desligamentos vs. período anterior
                  </div>
                </div>
              </div>
            </Section>
          )}

          {data.evolution.length > 0 && (
            <Section kicker="Tendência" title="Evolução mensal · Admissões × Desligamentos × Turnover %"
                     testId="tov-evolution">
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data.evolution} margin={{ top: 5, right: 8, bottom: 5, left: -8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DD" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="l" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} unit="%" />
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar yAxisId="l" dataKey="admissoes" fill="#3a7559" name="Admissões" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="l" dataKey="desligamentos" fill="#C5A059" name="Desligamentos" radius={[4, 4, 0, 0]} />
                    <Line yAxisId="r" type="monotone" dataKey="turnover_pct" stroke="#234935" strokeWidth={2.5} name="Turnover %" dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </Section>
          )}

          {yoyPivot && yoyPivot.rows.length > 0 && (
            <Section kicker="Year over Year" title="Turnover % por mês · comparativo entre anos"
                     testId="tov-yoy">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={yoyPivot.rows} margin={{ top: 5, right: 8, bottom: 5, left: -8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DD" vertical={false} />
                    <XAxis dataKey="month_label" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} unit="%" />
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {yoyPivot.years.map((yr, i) => (
                      <Line key={yr} type="monotone" dataKey={`y${yr}`}
                            stroke={i === 0 ? "#94A3B8" : "#234935"}
                            strokeWidth={i === 0 ? 1.8 : 2.5}
                            strokeDasharray={i === 0 ? "5 4" : ""}
                            name={`${yr}`} dot={{ r: 3 }} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Section>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Section kicker="Por categoria" title="Voluntário × Involuntário" testId="tov-breakdown">
              {breakdown.length === 0 ? (
                <div className="text-[12.5px] text-muted-foreground italic py-6 text-center">
                  <AlertCircle className="h-4 w-4 mx-auto mb-2 text-[#C5A059]" />
                  Nenhum desligamento no período selecionado.
                </div>
              ) : (
                <div className="space-y-2 mt-1">
                  {breakdown.map((b) => {
                    const total = breakdown.reduce((acc, x) => acc + x.value, 0);
                    const pct = Math.round((b.value / total) * 100);
                    return (
                      <div key={b.name}>
                        <div className="flex items-center justify-between text-[11.5px] mb-1">
                          <span className="font-medium" style={{ color: b.color }}>{b.name}</span>
                          <span className="font-mono tabular-nums">{b.value} · {pct}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: b.color }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Section>

            <div className="lg:col-span-2">
              <Section kicker="Ranking" title="Setores com maior turnover (período)" testId="tov-by-sector">
                <div className="overflow-x-auto">
                  <table className="w-full text-[12.5px]">
                    <thead>
                      <tr className="bg-slate-50 border-b border-border">
                        <th className="text-left px-3 py-2">#</th>
                        <th className="text-left px-3 py-2">Setor</th>
                        <th className="text-left px-3 py-2">Supervisor</th>
                        <th className="text-right px-3 py-2">Headcount</th>
                        <th className="text-right px-3 py-2">Desligados</th>
                        <th className="text-right px-3 py-2">Turnover %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_sector.slice(0, 10).map((s, i) => (
                        <tr key={s.sector_id} className="border-b border-border last:border-0">
                          <td className="px-3 py-2 text-muted-foreground">{i + 1}</td>
                          <td className="px-3 py-2 font-medium">{s.sector}</td>
                          <td className="px-3 py-2 text-muted-foreground text-[11px]">{s.responsavel || "—"}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{s.headcount}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{s.desligados_12m}</td>
                          <td className={`px-3 py-2 text-right tabular-nums font-semibold ${
                            s.turnover_pct >= 15 ? "text-rose-700" : s.turnover_pct >= 8 ? "text-amber-700" : "text-emerald-700"
                          }`}>
                            {s.turnover_pct}%
                          </td>
                        </tr>
                      ))}
                      {data.by_sector.length === 0 && (
                        <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground italic">Sem dados de turnover no período/filtros selecionados.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Section>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Section kicker="Cargos" title="Top cargos por desligamento" testId="tov-by-cargo">
              {data.by_cargo.length === 0 ? (
                <div className="text-[12.5px] text-muted-foreground italic py-4 text-center">Sem dados no período.</div>
              ) : (
                <ul className="divide-y divide-border">
                  {data.by_cargo.slice(0, 10).map((c) => (
                    <li key={c.cargo} className="flex items-center justify-between py-1.5 text-[12.5px]">
                      <span>{c.cargo}</span>
                      <span className="font-mono tabular-nums font-semibold">{c.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section kicker="Motivos" title="Distribuição por motivo" testId="tov-motivos">
              {data.motivos.length === 0 ? (
                <div className="text-[12.5px] text-muted-foreground italic py-4 text-center">Sem dados no período.</div>
              ) : (
                <ul className="divide-y divide-border">
                  {data.motivos.map((m) => (
                    <li key={m.motivo} className="flex items-center justify-between py-1.5 text-[12.5px]">
                      <span>{TIPO_LABEL[m.motivo] || m.motivo}</span>
                      <span className="font-mono tabular-nums font-semibold">{m.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </div>

          <div className="rounded-md bg-slate-50 border border-border px-3 py-2.5 text-[11px] text-slate-700 flex items-start gap-2">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 text-[#C5A059] shrink-0" />
            <div>
              <b>Fórmula:</b> Turnover = desligamentos do período ÷ headcount médio (início + fim do período / 2) × 100. <br />
              <b>Headcount histórico:</b> calculado AS-OF cada mês (colaboradores admitidos antes E ainda ativos no fim do mês).
            </div>
          </div>
        </>
      )}
    </div>
  );
}
